import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { QUESTIONS } from "../data/quizData";

export const Quiz = ({ step, onAnswer, onBack }) => {
  const question = QUESTIONS[step];
  const progress = ((step + 1) / QUESTIONS.length) * 100;

  return (
    <div data-testid={`quiz-screen-${question.id}`} className="flex flex-col flex-1">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <button
            data-testid="quiz-back-btn"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-[#85746C] hover:text-[#B25E3B] transition-colors duration-200 py-2 -ml-1"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <span
            data-testid="quiz-progress-label"
            className="text-xs uppercase tracking-[0.15em] font-semibold text-[#85746C]"
          >
            Question {step + 1} of {QUESTIONS.length}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-[#E9DFD6] overflow-hidden">
          <motion.div
            data-testid="quiz-progress-bar"
            className="h-full rounded-full bg-[#B25E3B]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2
            data-testid={`quiz-question-${question.id}`}
            className="font-serif-display text-2xl sm:text-3xl leading-snug text-[#2C221E] mb-2"
          >
            {question.title}
          </h2>
          <p className="text-base text-[#85746C] mb-8">{question.subtitle}</p>

          <div className={question.isScale ? "grid grid-cols-5 gap-2.5 sm:gap-3" : "flex flex-col gap-3.5"}>
            {question.options.map((option, idx) =>
              question.isScale ? (
                <motion.button
                  key={option.id}
                  data-testid={`quiz-option-${question.id}-${option.id}`}
                  onClick={() => onAnswer(question.key, parseInt(option.id, 10))}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-white border border-[#E9DFD6] py-5 sm:py-7 hover:border-[#B25E3B] hover:bg-[#FBEBE4]/40 transition-[border-color,background-color] duration-150 focus:outline-none focus:ring-2 focus:ring-[#B25E3B]"
                >
                  <span className="font-serif-display text-2xl sm:text-3xl text-[#B25E3B]">
                    {option.label}
                  </span>
                  <span className="text-[10px] sm:text-xs text-[#85746C] text-center leading-tight px-1">
                    {option.detail}
                  </span>
                </motion.button>
              ) : (
                <motion.button
                  key={option.id}
                  data-testid={`quiz-option-${question.id}-${option.id}`}
                  onClick={() => onAnswer(question.key, option.id)}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.985 }}
                  className="w-full text-left p-5 sm:p-6 rounded-2xl bg-white border border-[#E9DFD6] hover:border-[#B25E3B] hover:bg-[#FBEBE4]/40 hover:shadow-md transition-[border-color,background-color,box-shadow] duration-150 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#B25E3B]"
                >
                  <span className="block text-base sm:text-lg font-semibold text-[#2C221E]">
                    {option.label}
                  </span>
                  <span className="block text-sm text-[#85746C] mt-1">
                    {option.detail}
                  </span>
                </motion.button>
              )
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
